<template>
  <div class="transaction-management">
    <div class="header">
      <h2>交易记录</h2>
      <div class="actions">
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 交易列表 -->
    <el-card>
      <el-table :data="transactionList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="fromUser" label="发送方" width="120" />
        <el-table-column prop="toUser" label="接收方" width="120" />
        <el-table-column prop="amount" label="金额" width="120">
          <template #default="{ row }">
            {{ row.amount }} 积分
          </template>
        </el-table-column>
        <el-table-column prop="type" label="交易类型" width="120">
          <template #default="{ row }">
            <el-tag>{{ getTypeText(row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="交易时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 查看详情对话框 -->
    <el-dialog v-model="viewDialogVisible" title="交易详情" width="600px">
      <el-descriptions :column="2" border v-if="currentTransaction">
        <el-descriptions-item label="交易ID">{{ currentTransaction.id }}</el-descriptions-item>
        <el-descriptions-item label="交易状态">
          <el-tag :type="getStatusTag(currentTransaction.status)">
            {{ getStatusText(currentTransaction.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="发送方">{{ currentTransaction.fromUser }}</el-descriptions-item>
        <el-descriptions-item label="接收方">{{ currentTransaction.toUser }}</el-descriptions-item>
        <el-descriptions-item label="交易金额">
          <span class="amount-highlight">{{ currentTransaction.amount }} 积分</span>
        </el-descriptions-item>
        <el-descriptions-item label="交易类型">
          <el-tag>{{ getTypeText(currentTransaction.type) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="交易时间" :span="2">{{ formatDateTime(currentTransaction.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="交易描述" :span="2">{{ currentTransaction.description || '无' }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ currentTransaction.remark || '无' }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'

const loading = ref(false)
const viewDialogVisible = ref(false)
const currentTransaction = ref(null)

const pagination = reactive({
  current: 1,
  size: 10,
  total: 4
})

const transactionList = ref([
  { id: 1, fromUser: '张三', toUser: '李四', amount: 100, type: 'transfer', status: 'success', createdAt: '2026-03-05T10:30:00', description: '亲友帮还' },
  { id: 2, fromUser: '系统', toUser: '王五', amount: 50, type: 'reward', status: 'success', createdAt: '2026-03-05T09:15:00', description: '任务完成奖励' },
  { id: 3, fromUser: '赵六', toUser: '商户A', amount: 200, type: 'purchase', status: 'pending', createdAt: '2026-03-04T16:45:00', description: '消费返佣' },
  { id: 4, fromUser: '钱七', toUser: '系统', amount: 500, type: 'withdraw', status: 'success', createdAt: '2026-03-04T14:20:00', description: '积分提现' }
])

const getTypeText = (type) => {
  const map = { transfer: '转账', reward: '奖励', purchase: '消费', withdraw: '提现', deposit: '充值' }
  return map[type] || type
}

const getStatusText = (status) => {
  const map = { success: '成功', pending: '处理中', failed: '失败' }
  return map[status] || status
}

const getStatusTag = (status) => {
  const map = { success: 'success', pending: 'warning', failed: 'danger' }
  return map[status] || ''
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN')
}

const refreshData = () => {
  loading.value = true
  setTimeout(() => {
    loading.value = false
    ElMessage.success('数据已刷新')
  }, 500)
}

const handleView = (row) => {
  currentTransaction.value = row
  viewDialogVisible.value = true
}

const handleSizeChange = (size) => { pagination.size = size }
const handleCurrentChange = (page) => { pagination.current = page }

onMounted(() => refreshData())
</script>

<style scoped>
.transaction-management { padding: 20px; }
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.header h2 { margin: 0; font-size: 24px; color: #ffffff; }
.actions { display: flex; gap: 10px; }
.pagination { margin-top: 20px; display: flex; justify-content: flex-end; }
.amount-highlight { font-size: 18px; font-weight: bold; color: #409eff; }
</style>
